import { useTranslation } from "react-i18next";

import { useAuth } from "@/hooks/useAuth";
import { useLanguages } from "@/hooks/useLanguages";
import { SUPPORTED_LOCALES } from "@/i18n";
import i18n from "@/i18n";
import { updateProfile } from "@/services/api/auth";
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/feedback/ToastHost";

const LOCALE_NAMES: Record<string, string> = {
  en: "English",
  pl: "Polski",
  fr: "Français",
  es: "Español",
};

/**
 * The language selection submenu of the profile menu. The locale list is
 * driven by the registry (`/languages`), so a locale added in the admin is
 * available here without a rebuild. Selecting a locale switches i18n
 * immediately, loads its authoritative bundle, and persists the preference to
 * the profile so it is reapplied at startup.
 */
export function LanguagePicker() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const languages = useLanguages();

  const locales =
    languages.data && languages.data.length > 0
      ? languages.data.map((language) => language.code)
      : [...SUPPORTED_LOCALES];

  const change = async (locale: string) => {
    await i18n.changeLanguage(locale);
    if (user) {
      try {
        await updateProfile({ preferred_locale: locale });
      } catch {
        // Preference persistence is best-effort; the switch still applies.
      }
    }
    toast.success(t("nav.language") + ": " + (LOCALE_NAMES[locale] ?? locale));
  };

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>{t("nav.language")}</DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuLabel>{t("nav.language")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {locales.map((locale) => (
          <DropdownMenuItem
            key={locale}
            onSelect={() => void change(locale)}
            className={locale === (user?.preferred_locale ?? "en") ? "font-semibold" : undefined}
          >
            {LOCALE_NAMES[locale] ?? locale}
          </DropdownMenuItem>
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
